/**
 * The scanned supplier invoice behind one BAB booking.
 *
 * WHY THIS EXISTS RATHER THAN A LINK. Everywhere else in this app a DocuWare document is a
 * deep link into DocuWare's own web client, which needs the viewer's own DocuWare login. The
 * rule for this page is different — whoever may open the booking detail may see the document —
 * so the bytes come through our API instead, and this renders them. The deep link is still in
 * the footer for anyone who wants the workflow record, the index fields and the audit trail.
 *
 * TWO THINGS IT REFUSES TO PRETEND:
 *
 *  1. **A section is not "the invoice".** A DocuWare document is a staple of sections and their
 *     order carries no meaning — document 11486 is indexed as one invoice and holds the
 *     invoice that superseded it in section 3. So the tabs are numbered and never named, and
 *     the count comes from DWSECTIONCOUNT rather than from a guess.
 *  2. **A weak match looks weak.** Confidence travels from the matcher and is shown, with the
 *     same sentences /docuware-invoices uses, so the two pages do not disagree about the same
 *     document.
 *
 * The PDF is rendered from a blob: URL, not from the API URL directly, because the SWA sets a
 * global `X-Frame-Options: DENY` that refuses even same-origin framing. A blob carries no HTTP
 * headers, so it sidesteps that — at the cost of one CSP token (`frame-src 'self' blob:` in
 * web/public/staticwebapp.config.json). Revoke discipline is in the effect below: both on
 * change and on unmount, or you get a leak and eventually a blank viewer.
 */
import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, AlertTriangle, Download, FileText } from 'lucide-react';
import { babReportApi } from '../../services/babReportApi';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';
import { DocuWareDocLink } from '../docuware-invoices/DocuWareDocLink';
import type { BabPostingDocument } from '../../types/babReport';

interface Props {
    /** Every document filed under this booking's Beleg, strongest match first. */
    documents: BabPostingDocument[];
    baseUrl: string | null;
    /** The drill key — the server needs all of it to authorise the fetch. */
    year: number;
    month: number;
    lineCode: string;
    companyCode: string;
    buCode: string;
    accountCode: string;
    sourceId: string;
    /** What the user clicked, echoed in the header so the two can be compared at a glance. */
    documentNo: string | null;
    postingLabel: string;
    onClose: () => void;
}

/** Same sentences as `matchCaveat` on /docuware-invoices — one document, one story. */
function caveatFor(doc: BabPostingDocument, siblings: number): string | null {
    if (doc.matchMethod === 'conflict') {
        return 'DocuWare and Business Central name different posting documents for this invoice.';
    }
    if (siblings > 1) {
        return `${siblings} documents are filed under this posting document.`;
    }
    if (doc.matchMethod === 'belegnummer') {
        return 'Taken from DocuWare’s own reference; no matching Business Central entry confirmed it.';
    }
    if (doc.matchMethod === 'externaldoc') {
        return 'Matched on the supplier invoice number; DocuWare carries no posting reference.';
    }
    return null;
}

function formatAmount(amount: number | null, currency: string | null): string | null {
    if (amount === null) return null;
    const n = amount.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${n} ${currency}` : n;
}

/** 'YYYY-MM-DD' -> 'DD.MM.YYYY'. String in, string out: no Date, no timezone to lose a day to. */
function formatDate(value: string | null): string | null {
    return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split('-').reverse().join('.') : value;
}

export function BabDocumentViewerModal({
    documents, baseUrl, year, month, lineCode, companyCode, buCode, accountCode,
    sourceId, documentNo, postingLabel, onClose,
}: Props) {
    const [docIndex, setDocIndex] = useState(0);
    const [section, setSection] = useState(1);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useTopmostEscape(true, onClose);

    const doc = documents[docIndex];
    const caveat = useMemo(
        () => (doc ? caveatFor(doc, documents.length) : null),
        [doc, documents.length]
    );

    // Switching documents restarts at section 1 — section 3 of one document has nothing to do
    // with section 3 of another.
    useEffect(() => { setSection(1); }, [docIndex]);

    useEffect(() => {
        if (!doc) return;
        let active = true;
        let created: string | null = null;
        setLoading(true);
        setError(null);
        setBlobUrl(null);

        const url = babReportApi.documentPdfUrl(
            year, month, lineCode, companyCode, buCode, accountCode,
            sourceId, doc.documentId, section
        );
        babReportApi.fetchDocumentPdf(url)
            .then(blob => {
                if (!active) return;
                created = URL.createObjectURL(blob);
                setBlobUrl(created);
            })
            .catch(err => {
                if (active) setError(err instanceof Error ? err.message : 'The document could not be opened.');
            })
            .finally(() => { if (active) setLoading(false); });

        // Revoked on every change AND on unmount. Revoking right after assigning it to the
        // iframe instead would race the browser's own PDF plugin and show an empty frame.
        return () => {
            active = false;
            if (created) URL.revokeObjectURL(created);
        };
    }, [doc, section, year, month, lineCode, companyCode, buCode, accountCode, sourceId]);

    if (!doc) return null;

    const sections = Array.from({ length: Math.max(1, doc.sectionCount) }, (_, i) => i + 1);
    const mixedTypes = doc.extension === '.';
    const amount = formatAmount(doc.amount, doc.currency);
    const fileName = `${companyCode}_${documentNo || sourceId}_s${section}.pdf`.replace(/[^a-zA-Z0-9_.-]/g, '_');

    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                className="bg-white rounded-lg shadow-xl w-[92vw] max-w-[1100px] h-[90vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <FileText className="h-5 w-5 text-gray-400 shrink-0" />
                            <span className="truncate">{doc.supplier || 'Supplier not indexed'}</span>
                            {doc.matchConfidence !== null && doc.matchConfidence < 100 && (
                                <span className="shrink-0 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                                    weak match ({doc.matchMethod})
                                </span>
                            )}
                        </h3>
                        <p className="mt-0.5 text-sm text-gray-500 truncate">
                            {[
                                doc.invoiceNumber && `Invoice ${doc.invoiceNumber}`,
                                formatDate(doc.invoiceDate),
                                amount,
                                documentNo && `Beleg ${documentNo}`,
                            ].filter(Boolean).join(' · ')}
                        </p>
                        {/* The booking, so a supplier that makes no sense for this cost line is
                            visible without opening anything. */}
                        <p className="mt-0.5 text-xs text-gray-400 truncate">Booking: {postingLabel}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="ml-4 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        title="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {(caveat || mixedTypes) && (
                    <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-800">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                            {caveat && <p>{caveat}</p>}
                            {mixedTypes && <p>This document mixes file types; some sections may not open as PDF.</p>}
                        </div>
                    </div>
                )}

                {(documents.length > 1 || sections.length > 1) && (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-gray-200 px-6 py-2">
                        {documents.length > 1 && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Document</span>
                                {documents.map((d, i) => (
                                    <button
                                        key={d.documentId}
                                        onClick={() => setDocIndex(i)}
                                        className={`rounded px-2 py-0.5 text-xs ${i === docIndex
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        title={d.invoiceNumber || undefined}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                            </div>
                        )}
                        {sections.length > 1 && (
                            <div className="flex items-center gap-2">
                                {/* Numbered, never named. Section order carries no meaning. */}
                                <span
                                    className="text-xs text-gray-500"
                                    title="A document is a staple of sections; their order carries no meaning."
                                >
                                    Section
                                </span>
                                {sections.map(n => (
                                    <button
                                        key={n}
                                        onClick={() => setSection(n)}
                                        className={`rounded px-2 py-0.5 text-xs ${n === section
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        {n}
                                    </button>
                                ))}
                                <span className="text-xs text-gray-400">of {sections.length}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex-1 overflow-hidden bg-gray-100 p-3">
                    {loading && (
                        <div className="flex h-full items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                            <span className="ml-2 text-gray-500">Loading document…</span>
                        </div>
                    )}
                    {!loading && error && (
                        <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                            <AlertTriangle className="h-8 w-8 text-amber-500" />
                            {/* The endpoint's own sentence, verbatim — it knows why. */}
                            <p className="mt-3 max-w-lg text-sm text-gray-700">{error}</p>
                            {doc.documentUrl && (
                                <a
                                    href={doc.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                                >
                                    Open in DocuWare instead
                                </a>
                            )}
                        </div>
                    )}
                    {!loading && !error && blobUrl && (
                        <iframe
                            key={blobUrl}
                            src={blobUrl}
                            title={fileName}
                            className="h-full w-full rounded border border-gray-300 bg-white"
                        />
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
                    <div className="flex items-center gap-4">
                        <DocuWareDocLink
                            row={{
                                documentUrl: doc.documentUrl,
                                documentUrlVerified: doc.documentUrlVerified,
                                baseUrl,
                                docId: doc.docId,
                            }}
                            onCopyDocId={id => void navigator.clipboard?.writeText(id)}
                            variant="inline"
                        />
                    </div>
                    {blobUrl && (
                        <a
                            href={blobUrl}
                            download={fileName}
                            className="inline-flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <Download className="h-4 w-4" />
                            Download
                        </a>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
